import {
    ApiResponse,
    CreateLabelRequest,
    CreateLabelResponse,
    GetAutoplanTutorialStatusResponse,
    GetBillingDataResponse,
    GetLabelsResponse,
    GetModalFlagsResponse,
    SaveBillingDataRequest,
    UpdateModalFlagsRequest,
} from "src/model/api/v2/data-contracts";
import { TAG_AUTOPLAN_TUTORIAL_STATUS, TAG_BILLING_DATA, TAG_GET_USER_LABELS, TAG_PROJECTS, TAG_USER_INFO } from "src/api/api-tags";
import { appApiSlice } from "src/api/app-ApiSlice";

interface UserInfoResponse {
    firstName: string;
    lastName: string;
    userAvatarLink: string;
    hasPro: boolean;
    daysLeft: number | null;
    siteLanguage: string;
}

export const userV2ApiSlice = appApiSlice.injectEndpoints({
    endpoints: builder => ({
        getLabels: builder.query<GetLabelsResponse, void>({
            query: () => ({
                url: 'v2/user/get-labels',
                method: 'GET',
            }),
            providesTags: [{ type: TAG_GET_USER_LABELS }],
        }),
        createLabel: builder.mutation<CreateLabelResponse, CreateLabelRequest>({
            query(labelData: CreateLabelRequest) {
                return {
                    url: 'v2/user/create-label',
                    method: 'POST',
                    body: labelData,
                };
            },
            invalidatesTags: [{ type: TAG_GET_USER_LABELS }],
        }),
        getUserInfo: builder.query<UserInfoResponse, void>({
            query() {
                return `v2/user/get-user-info`;
            },
            providesTags: [TAG_USER_INFO],
        }),
        deleteUserLabel: builder.mutation<void, { labelId: number }>({
            query: ({ labelId }) => ({
                url: `v2/user/delete-user-label/${labelId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [TAG_GET_USER_LABELS, TAG_PROJECTS]
        }),
        getModalFlags: builder.query<GetModalFlagsResponse, void>({
            query: () => `v2/user/get-modal-flags`,
        }),
        updateModalFlags: builder.mutation<ApiResponse, UpdateModalFlagsRequest>({
            query: (body: UpdateModalFlagsRequest) => ({
                url: `v2/user/update-modal-flags`,
                method: 'PATCH',
                body,
            }),
        }),
        getBillingData: builder.query<GetBillingDataResponse, void>({
            query: () => `v2/user/get-billing-data`,
            providesTags: [{ type: TAG_BILLING_DATA }],
        }),
        saveBillingData: builder.mutation<ApiResponse, SaveBillingDataRequest>({
            query: (body: SaveBillingDataRequest) => ({
                url: `v2/user/save-billing-data`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: TAG_BILLING_DATA }],
        }),
        getAutoplanTutorialStatus: builder.query<GetAutoplanTutorialStatusResponse, void>({
            query: () => `v2/user/get-autoplan-tutorial-status`,
            providesTags: [{ type: TAG_AUTOPLAN_TUTORIAL_STATUS }],
        }),
        setAutoplanTutorialComplete: builder.mutation<ApiResponse, void>({
            query: () => ({
                url: `v2/user/set-autoplan-tutorial-complete`,
                method: 'POST',
            }),
            invalidatesTags: [{ type: TAG_AUTOPLAN_TUTORIAL_STATUS }],
        }),
    })
})

export const {
    useGetLabelsQuery,
    useCreateLabelMutation,
    useGetUserInfoQuery,
    useDeleteUserLabelMutation,
    useGetModalFlagsQuery,
    useUpdateModalFlagsMutation,
    useGetAutoplanTutorialStatusQuery,
    useSetAutoplanTutorialCompleteMutation,
    useGetBillingDataQuery,
    useSaveBillingDataMutation,
} = userV2ApiSlice;